import { forwardRef, useEffect, useRef } from "react";
import { useController } from "react-hook-form";
import { Input } from "antd";
import styles from "./styles.module.scss";
import classNames from "classnames";
import type { OTPProps, OTPRef } from "antd/es/input/OTP";
import { IDS } from "src/utils/enums";
import { ARIA_LABEL, ARIA_LABEL_CELL } from "src/utils/consts";
import { CustomInputType } from "src/utils/types";
import { useAppSelector } from "src/hooks/useRedux";
import { __DEV__ } from "src/utils/utils";

declare global {
  interface CredentialRequestOptions {
    otp?: { transport: string[] };
  }
}

interface OTPCredential extends Credential {
  code: string;
}

interface CustomInputProps {
  inputType: CustomInputType;
  maxLength: number;
  minLength: number;
  name: string;
  customStyle?: string;
  type: string;
  isOTP?: boolean;
  ariaDescribedBy?: IDS;
  errorDescribedBy?: IDS;
  autoFocus?: boolean;
}

const CustomInput = forwardRef<OTPRef, CustomInputProps>(
  ({ inputType, maxLength, minLength, name, customStyle, type, isOTP = false, ariaDescribedBy, errorDescribedBy, autoFocus = false }, ref) => {
    const { strings } = useAppSelector((state) => state.app);

    const {
      field: { value, onChange, onBlur },
    } = useController({
      name,
      defaultValue: "",
    });

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isOTP || !("OTPCredential" in window)) return;

      const abortController = new AbortController();

      async function handleOTPReceive() {
        try {
          const credential = (await navigator.credentials.get({
            otp: { transport: ["sms"] },
            signal: abortController.signal,
          })) as OTPCredential;

          onChange(credential.code);
        } catch (error: any) {
          if (error.name !== "AbortError") {
            __DEV__ && console.log("WebOTP API error:", error);
          }
        }
      }

      handleOTPReceive();

      return () => {
        abortController.abort();
      };
    }, [type, onChange]);

    // Set aria attributes on individual OTP boxes after rendering
    useEffect(() => {
      if (containerRef.current) {
        const inputs = containerRef.current.querySelectorAll<HTMLInputElement>(
          ".ant-input"
        );
        inputs.forEach((input, index) => {
          input.setAttribute("aria-label", `${ARIA_LABEL_CELL[inputType]} ${strings.acc_cell_number} ${index + 1}`);
          input.setAttribute("aria-required", "true");
        });
      }
    }, [containerRef]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
      // Allow only numeric keys, backspace, delete, arrow keys, etc.
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter",
      ];
      const isNumberKey = /^[0-9]$/.test(e.key);
      // Allow copy/paste
      if ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'v')) {
        return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
      }

      if (!isNumberKey && !allowedKeys.includes(e.key)) {
        e.preventDefault(); // Block other keys
      }
    };


    return (
      <fieldset className={styles.idInput}>
        <legend className="srOnly">
          {`${ARIA_LABEL[inputType]} - ${strings.acc_enter} ${maxLength} ${strings.acc_numbers}`}
        </legend>
        <div className={styles.border}>
          <div className={classNames(styles.inputContainer, customStyle)} ref={containerRef} >
            <Input.OTP
              {...({
                value,
                onChange,
                onKeyDown: handleKeyPress,
                length: maxLength,
                className: styles.hiddenInput,
                ref,
                onBlur,
                autoFocus,
                type,
                inputMode: "numeric",
                "aria-required": "true",
                "aria-invalid": value?.length < minLength,
                "aria-describedby": `${ariaDescribedBy}` + (errorDescribedBy ? " " + errorDescribedBy : ""),
                style: {
                  width: "100%",
                  justifyContent: "center",
                },
                autoComplete: "one-time-code",
                "data-testid": "otp-input",
              } as OTPProps & { autoComplete?: string, "data-testid"?: string })}
            />
          </div>
        </div>
        <div id={`${name}-description`} className="srOnly">

          {strings.t_digit_entry_message?.replace("{{maxLength}}", maxLength.toString())?.replace("{{enteredLength}}", value?.length || 0)?.toString()}
        </div>
        {isOTP && (
          <span id="otp-format" style={{ display: 'none' }}>
            {strings.t_otp_format}
          </span>
        )}
      </fieldset>
    );
  }
);

export default CustomInput;
